/**
 * Sentry SDK initialization for client-side error tracking.
 * Optional: only initializes when VITE_SENTRY_DSN is set.
 * Redacts PII via beforeSend; sets release, environment.
 * @see .Cursor/plans/error_logging_&_error_ticket_workflow
 */

import * as Sentry from '@sentry/react';

const JWT_PATTERN = /eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g;

function redactString(s) {
  if (typeof s !== 'string') return s;
  return s.replace(JWT_PATTERN, '[REDACTED_JWT]');
}

function redactEvent(event) {
  if (!event) return event;
  const redacted = { ...event };

  if (redacted.exception?.values) {
    redacted.exception = {
      ...redacted.exception,
      values: redacted.exception.values.map((exc) => ({
        ...exc,
        value: exc.value ? redactString(String(exc.value)) : exc.value,
      })),
    };
  }

  if (redacted.message) {
    redacted.message = redactString(redacted.message);
  }

  return redacted;
}

/**
 * Initialize Sentry for the React client. Call before ReactDOM.createRoot.
 * No-op when VITE_SENTRY_DSN is not set.
 * @returns {boolean} True if Sentry was initialized
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN?.trim();
  if (!dsn) {
    return false;
  }

  const release =
    typeof __APP_VERSION__ !== 'undefined'
      ? `sparxmate-client@${__APP_VERSION__}`
      : undefined;

  Sentry.init({
    dsn,
    release,
    environment: import.meta.env.MODE || 'development',
    enableLogs: true,
    beforeSend(event) {
      return redactEvent(event);
    },
    // Omit BrowserSession so we do not POST a session envelope on every load (often blocked as tracking).
    defaultIntegrations: Sentry.getDefaultIntegrations().filter(
      (integration) => integration.name !== 'BrowserSession'
    ),
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    ignoreErrors: ['ResizeObserver loop limit exceeded'],
  });

  return true;
}

export { Sentry };
